import { Button } from "@/components/ui/button"
import { useEffectEvent, useState } from "react"
import { Link, useNavigate, useRouterState } from "@tanstack/react-router"
import {
	ClipboardList,
	CreditCard,
	FlaskConical,
	Headphones,
	LayoutDashboard,
	Package,
	ScrollText,
	Settings,
	Users,
	Volume2,
	VolumeX,
	X,
	type LucideIcon,
} from "lucide-react"

import { usePortalAuth } from "@/features/authz/hooks/use-portal-auth"
import { Switch } from "@/components/ui/switch"
import { getAuthenticatedTokens, logout } from "@/lib/auth/auth-store"
import { getInteractionSoundsEnabled, setInteractionSoundsEnabled } from "@/lib/interaction-sounds"
import type { PortalAuthMe } from "@optimist/schemas"

type NavItem = {
	to: string
	icon: LucideIcon
	label: string
	disabled?: boolean
}

export function buildNavItems(auth: PortalAuthMe | undefined): NavItem[] {
	const permissions = new Set(auth?.permissions ?? [])
	const items: NavItem[] = [{ to: "/dashboard", icon: LayoutDashboard, label: "Overview" }]

	if (permissions.has("orders.read")) items.push({ to: "/dashboard/orders", icon: ClipboardList, label: "Orders" })
	if (permissions.has("patients.read.basic")) items.push({ to: "/dashboard/patients", icon: Users, label: "Patients" })
	if (permissions.has("payments.read")) items.push({ to: "/dashboard/payments", icon: CreditCard, label: "Payments" })
	if (permissions.has("products.read") || permissions.has("products.manage")) {
		items.push({ to: "/dashboard/products", icon: Package, label: "Products" })
	}
	if (permissions.has("audit.read")) items.push({ to: "/dashboard/audit", icon: ScrollText, label: "Audit" })

	if (permissions.has("orders.read")) items.push({ to: "/dashboard/labs/", icon: FlaskConical, label: "Labs" })
	items.push({ to: "/dashboard/support", icon: Headphones, label: "Support", disabled: true })
	if ((auth !== undefined && auth.roles.includes("super_admin")) || permissions.has("lab.ai.settings.read") || permissions.has("access.roles.read") || permissions.has("access.assignments.read") || (permissions.has("orders.read") && permissions.has("patients.read.phi"))) {
		items.push({ to: "/dashboard/settings", icon: Settings, label: "Settings" })
	}

	return items
}

type AppSidebarProps = {
	isOpen: boolean
	onClose: () => void
}

type LinkParts = {
	path: string
	search: string
}

function normalizePath(pathname: string): string {
	if (pathname === "/") return "/"
	return pathname.replace(/\/+$/, "")
}

function splitLink(to: string): LinkParts {
	const parts = to.split("?")
	const rawPath = parts[0]
	if (typeof rawPath !== "string" || rawPath.length === 0) {
		throw new Error("Invalid navigation path")
	}
	const path = normalizePath(rawPath)
	const rawSearch = parts.length > 1 ? parts[1] : undefined
	const search = typeof rawSearch === "string" ? `?${rawSearch}` : ""
	return { path, search }
}

function isRouteActive(currentPathname: string, currentSearch: string, to: string): boolean {
	const currentPath = normalizePath(currentPathname)
	const { path, search } = splitLink(to)
	if (path === "/") return currentPath === "/"
	if (path === "/dashboard") return currentPath === "/dashboard"
	const pathMatches = currentPath === path || currentPath.startsWith(`${path}/`)
	if (!pathMatches) return false
	if (search) return currentSearch === search
	return true
}

function decodeBase64UrlToUtf8(input: string): string {
	const b64 = input.replace(/-/g, "+").replace(/_/g, "/")
	const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, "=")
	return atob(padded)
}

function getUserEmailFromJwt(jwt: string): string {
	const parts = jwt.split(".")
	if (parts.length !== 3) {
		throw new Error("Invalid JWT format for user email")
	}

	const payloadB64 = parts[1]
	if (typeof payloadB64 !== "string" || payloadB64.length === 0) {
		throw new Error("Missing JWT payload segment for user email")
	}
	let payloadRaw: string
	try {
		payloadRaw = decodeBase64UrlToUtf8(payloadB64)
	} catch {
		throw new Error("Unable to decode JWT payload for user email")
	}

	let payload: unknown
	try {
		payload = JSON.parse(payloadRaw)
	} catch {
		throw new Error("Invalid JWT payload for user email")
	}

	if (!payload || typeof payload !== "object") {
		throw new Error("Missing JWT payload for user email")
	}

	const email = (payload as Record<string, unknown>)["email"]
	if (typeof email !== "string" || email.length === 0) {
		throw new Error("Missing email claim in access token")
	}
	return email
}

function getInitialsFromEmail(email: string): string {
	const namePart = email.split("@")[0]
	if (typeof namePart !== "string" || namePart.length === 0) {
		throw new Error("Missing email username segment")
	}
	return namePart.slice(0, 2).toUpperCase()
}

export function AppSidebar({ isOpen, onClose }: AppSidebarProps) {
	const [interactionSoundsEnabled, setInteractionSoundsPreference] = useState(getInteractionSoundsEnabled)
	const navigate = useNavigate()
	const location = useRouterState({
		select: (s) => ({ pathname: s.location.pathname, search: s.location.searchStr }),
	})
	const pathname = location.pathname
	const search = location.search
	const authQuery = usePortalAuth()
	const tokens = getAuthenticatedTokens()
	if (!tokens) {
		throw new Error("Missing auth tokens in sidebar")
	}
	const userEmail = getUserEmailFromJwt(tokens.accessToken)
	const userInitials = getInitialsFromEmail(userEmail)
	const navItems = buildNavItems(authQuery.data)

	const handleLinkClick = useEffectEvent(() => {
		onClose()
	})

	const handleLogout = useEffectEvent(() => {
		void logout().then(() => {
			navigate({ to: "/" })
		})
	})

	const handleInteractionSoundsChange = useEffectEvent((enabled: boolean) => {
		setInteractionSoundsPreference(enabled)
		setInteractionSoundsEnabled(enabled)
	})

	return (
		<aside
			className={`fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 lg:transform-none ${
				isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
			} bg-sidebar text-sidebar-foreground`}
		>
			<div className="flex flex-col h-full">
				<div className="shrink-0 px-6 py-6 border-b border-sidebar-border/20">
					<div className="flex items-center justify-between">
						<Link to="/dashboard" preload="intent" className="flex items-center gap-3" onClick={handleLinkClick}>
							<div className="flex size-8 items-center justify-center">
								<img src="/images/spray-pattern-white.svg" alt="OptiMist" width={32} height={32} />
							</div>
							<div>
								<p className="font-inter text-section-title font-semibold text-sidebar-foreground">OptiMist</p>
								<p className="font-inter text-meta font-normal text-sidebar-foreground/60">Admin Portal</p>
							</div>
						</Link>
						<Button size="icon" variant="secondary"
							type="button"
							onClick={onClose}
							className="lg:hidden"
							aria-label="Close navigation"
						>
							<X size={18} strokeWidth={1.5} />
						</Button>
					</div>
				</div>

				<nav className="flex-1 min-h-0 px-4 py-6 space-y-3 overflow-y-auto">
					{navItems.map((item) => {
						const active = isRouteActive(pathname, search, item.to)
						const isDisabled = item.disabled === true

						if (isDisabled) {
							return (
								<div
									key={item.to}
									className="flex items-center gap-3 px-4 py-3 rounded-xl opacity-40 cursor-not-allowed"
									title="Coming soon"
								>
									<item.icon size={20} strokeWidth={1.5} className="text-sidebar-foreground/40" />
									<span className="text-body font-normal text-sidebar-foreground/40">
										{item.label}
									</span>
								</div>
							)
						}

						return (
							<Link
								key={item.to}
								to={item.to}
								preload="intent"
								onClick={handleLinkClick}
								data-cuelume-hover="tick"
								className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
									active ? "bg-white/10" : "hover:bg-white/5"
								}`}
							>
								<item.icon
									size={20}
									strokeWidth={1.5}
									className={active ? "text-white" : "text-sidebar-foreground/60"}
								/>
								<span
									className={`text-body ${
										active ? "font-semibold text-white" : "font-normal text-sidebar-foreground/60"
									}`}
								>
									{item.label}
								</span>
							</Link>
						)
					})}
				</nav>

				<div className="shrink-0 px-4 py-4 border-t border-sidebar-border/20 space-y-2">
					<div className="flex items-center justify-between gap-3 px-4 py-2">
						<div className="flex min-w-0 items-center gap-3 text-sidebar-foreground/80">
							{interactionSoundsEnabled ? (
								<Volume2 aria-hidden="true" size={18} strokeWidth={1.5} />
							) : (
								<VolumeX aria-hidden="true" size={18} strokeWidth={1.5} />
							)}
							<span className="truncate text-meta font-medium">Interaction sounds</span>
						</div>
						<Switch
							checked={interactionSoundsEnabled}
							onCheckedChange={handleInteractionSoundsChange}
							aria-label="Interaction sounds"
						/>
					</div>
					<div className="flex items-center gap-3 px-4 py-3">
						<div className="w-9 h-9 rounded-full flex items-center justify-center bg-sidebar-primary text-sidebar-primary-foreground text-body font-semibold">
							{userInitials}
						</div>
						<div className="flex-1 min-w-0">
							<p className="truncate text-meta font-medium text-white">{userEmail}</p>
							<p className="truncate text-chip text-sidebar-foreground/60">Staff account</p>
						</div>
					</div>
					<Button variant="secondary"
						type="button"
						onClick={handleLogout}
						className="w-full"
					>
						Sign out
					</Button>
				</div>
			</div>
		</aside>
	)
}
